import { useState, useEffect, useRef } from "react";
import { fetchBrokerages, getCoordinate } from "../api";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Icon } from "leaflet";
import bluePinPng from "leaflet/dist/images/marker-icon.png";
import RankingTable from "../components/RankingTable";

function CenterOnCoord({ coord, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!coord) return;
    map.setView([coord.lat, coord.lng], zoom, { animate: true });
  }, [coord, zoom, map]);
  return null;
}

export default function SearchByAddress({ search, setSearch, buildingType }) {
  const [brokerages, setBrokerages] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [center, setCenter] = useState(null);
  const [coord, setCoord] = useState(null);
  const [summaryMsg, setSummaryMsg] = useState("");
  const lastQueryRef = useRef("");

  const performSearch = async (query) => {
    if (!query) return;
    lastQueryRef.current = query;

    setLoading(true);
    setSummaryMsg("");
    setCoord(null);
    setCenter(null);
    setListings([]);
    setBrokerages([]);

    try {
      const coordinateResult = await getCoordinate(query);
      if (!coordinateResult) throw new Error("Invalid address");

      const data = await fetchBrokerages({
        lat: coordinateResult.lat,
        lng: coordinateResult.lng,
        buildingType: buildingType === "All" ? null : buildingType,
        offset: 0,
        limit: 10000,
      });

      const soldListings = data
        .flatMap((b) => (b.listings || []).filter((l) => l.status === "sold"))
        .filter((l) => typeof l.lat === "number" && typeof l.lng === "number");

      setListings(soldListings);
      setBrokerages(data);
      setSummaryMsg(
        `${data.length} brokerages identified with a total of ${soldListings.length} sold listings` +
          (buildingType && buildingType !== "All"
            ? ` for type "${buildingType}"`
            : "")
      );
      setCoord(coordinateResult);
      setCenter([coordinateResult.lat, coordinateResult.lng]);
    } catch (err) {
      console.error(err);
      setListings([]);
      setBrokerages([]);
      setCenter(null);
      setCoord(null);
      setSummaryMsg(
        err.message.includes("Invalid")
          ? `Invalid address: "${query}"`
          : `Failed to fetch brokerages. Please try another address.`
      );
    } finally {
      setLoading(false);
    }
  };

  // Perform search when buildingType changes or on initial mount
  useEffect(() => {
    const query =
      lastQueryRef.current?.trim() || search?.trim() || "Toronto Downtown";
    performSearch(query);
  }, [buildingType]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    performSearch(search.trim());
  };

  return (
    <>
      <form onSubmit={handleSearch} className="flex gap-2 mb-6 justify-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brokerage by address"
          className="border border-gray-300 rounded px-3 py-2 w-80 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white placeholder-gray-400 text-gray-900"
        />
        <button
          type="submit"
          className="bg-white text-blue-600 px-4 py-2 rounded hover:bg-gray-200 transition font-semibold border border-blue-600"
        >
          Search
        </button>
      </form>

      {lastQueryRef.current && summaryMsg && (
        <div className="bg-blue-600/80 text-white text-center font-semibold py-2 px-4 rounded shadow-md space-y-1">
          {coord && (
            <div className="text-sm md:text-base">
              Showing results for:{" "}
              <span className="font-bold">{lastQueryRef.current}</span>
            </div>
          )}
          <div className="text-sm md:text-base">{summaryMsg}</div>
        </div>
      )}

      {loading && <p className="text-white text-center">Loading rankings...</p>}

      {!loading && center && listings.length > 0 && (
        <div className="flex w-full gap-6">
          <div className="bg-white/90 rounded-xl shadow-md p-6 w-1/2">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">
              Listings Map
            </h3>
            <div className="w-full h-96 rounded overflow-hidden">
              <MapContainer
                center={center}
                zoom={12}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {listings.map((listing) => (
                  <Marker
                    key={listing.id}
                    position={[listing.lat, listing.lng]}
                    icon={
                      new Icon({
                        iconUrl: bluePinPng,
                        iconSize: [25, 41],
                        iconAnchor: [12, 41],
                      })
                    }
                  >
                    <Popup>
                      <span className="font-semibold text-red-600">SOLD</span>
                      <br />${listing.price}
                      <br />
                      {listing.address}
                    </Popup>
                  </Marker>
                ))}
                <CenterOnCoord coord={coord} zoom={14} />
              </MapContainer>
            </div>
          </div>

          <div className="bg-white/90 rounded-xl shadow-md p-6 flex-1 min-w-[400px] overflow-x-auto">
            <RankingTable brokerages={brokerages} />
          </div>
        </div>
      )}
    </>
  );
}
